// read_aloud_passage — a paragraph for the learner to read, with optional
// narration and an inline Tashkeel toggle (advanced learners can dial the
// diacritics down without changing their global mode). Presentational.

import { useEffect, useState, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { stripTashkeel, type TashkeelMode } from '../tashkeel';
import { AudioButton } from './primitives';
import type { ActivityRendererProps } from './types';

const MODES: TashkeelMode[] = ['with', 'partial', 'without'];

export function ReadAloudPassage({
  data,
  ctx,
  cb,
}: ActivityRendererProps<content.ReadAloudPassageData>): ReactElement {
  const { t } = ctx;
  useEffect(() => {
    cb.onViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-passage override, seeded from the learner's global mode.
  const [mode, setMode] = useState<TashkeelMode>(ctx.tashkeelMode);
  const body = stripTashkeel(t(data.bodyKey), mode);

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col">
      {data.titleKey ? (
        <h2 className="mb-4 text-center text-2xl font-bold text-burgundy-700">
          <bdi>{stripTashkeel(t(data.titleKey), mode)}</bdi>
        </h2>
      ) : null}

      <article className="rounded-3xl bg-white p-6 text-2xl leading-loose text-ink-900 ring-1 ring-cream-200">
        <bdi>{body}</bdi>
      </article>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <AudioButton src={data.audio} ctx={ctx} label={t('content.common.play_audio')} />

        {data.allowTashkeelToggle ? (
          <div
            className="inline-flex overflow-hidden rounded-full ring-1 ring-cream-200"
            role="group"
            aria-label={t('content.read_aloud.tashkeel_label')}
          >
            {MODES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                aria-pressed={mode === m}
                className={`min-h-[44px] px-4 text-sm font-bold transition-colors ${
                  mode === m
                    ? 'bg-burgundy-500 text-cream-50'
                    : 'bg-white text-ink-700 hover:bg-cream-100'
                }`}
              >
                {t(`content.read_aloud.mode_${m}`)}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
